import { colors } from '@resurs/midas'
import { Column, TextInput } from '@resurs/midas-react'
import { Conditional } from '@resurs/react-utils'
import React from 'react'
import PropTypes from 'prop-types'
import NumberFormat from 'react-number-format'
import { connect } from 'react-redux'
import CurrencyInput from '../../../../../internal/CurrencyInput'
import IconButton from '../../../../../internal/IconButton'
import { getText } from '../../../../../internal/TextLabels'
import { PaymentMethods } from '../../../../../models/payment-methods'
import {
  setDebitAmount,
  setDebitReference,
} from '../../../../../store/actions/pay-apply-actions'
import { actOnData } from '../../../../../util/helpers'
import { isSigned } from '../../../../../util/paymentModel'
import Frame from '../Frame'
import styles from './styles.module.scss'

const Row = ({ label, children, ...props }) => (
  <div
    {...props}
    style={{
      display: 'flex',
      fontSize: '1.6rem',
      height: '2rem',
      ...props.style,
    }}
  >
    <Column noPadding col5>
      {getText(label)}
    </Column>
    <Column noPadding col7>
      <div
        style={{
          fontWeight: '500',
          textAlign: 'right',
          position: 'relative',
          paddingRight: '4rem',
        }}
      >
        {children}
      </div>
    </Column>
  </div>
)

Row.propTypes = {
  children: PropTypes.node.isRequired,
}

const InvoiceAmountBox = ({ result }) => (
  <Frame>
    <Row label="payApply.sign.invoiceAmount">
      <NumberFormat
        value={result.application.requestedAmount}
        displayType="text"
        thousandSeparator=" "
        renderText={val => getText('default.currency', val)}
      />
    </Row>
  </Frame>
)

InvoiceAmountBox.propTypes = {
  result: PropTypes.object.isRequired,
}

const DebitAmountRow = ({
  result,
  disabled,
  isValid = val => !!val,
  debitAmount,
  debitReference,
  minPurchaseLimit,
  changeAmount,
  changeReference,
}) =>
  actOnData(result.payment, ({ paymentStatus: { totalOrderAmount } }) => (
    <>
      <Row label="payApply.sign.debitAmount">
        <Conditional when={disabled}>
          <NumberFormat
            value={totalOrderAmount}
            displayType="text"
            thousandSeparator=" "
            renderText={val => getText('default.currency', val)}
          />
        </Conditional>
        <Conditional when={!disabled}>
          <div className={styles.debitAmount}>
            <CurrencyInput
              id="debit-amount-input"
              placeholderRange
              disabled={isSigned(result)}
              boundaries={{
                min: {
                  value: Math.max(1, minPurchaseLimit),
                  error: getText('payApply.paymentMethod.creditToSmall'),
                },
                max: {
                  value: result.application.approvedAmount,
                  error: getText('payApply.paymentMethod.newCreditNeededText'),
                },
              }}
              isValid={validation => isValid(validation)}
              onValueChange={value => changeAmount(value.floatValue)}
              value={debitAmount}
              fixedDecimalScale
              allowDecimals
            />
          </div>
        </Conditional>
      </Row>
      <Conditional when={!disabled}>
        <Row label="payApply.sign.debitReference">
          <div className={styles.debitReference}>
            <TextInput
              id="debit-reference"
              type="text"
              disabled={isSigned(result)}
              value={debitReference}
              onChange={e => changeReference(e.target.value)}
              skipValidation
            />
          </div>
        </Row>
      </Conditional>
    </>
  ))

DebitAmountRow.propTypes = {
  result: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired,
  isValid: PropTypes.func,
  debitAmount: PropTypes.number.isRequired,
  debitReference: PropTypes.string,
  minPurchaseLimit: PropTypes.number.isRequired,
  changeAmount: PropTypes.func.isRequired,
  changeReference: PropTypes.func.isRequired,
}

const ConnectedDebitAmountRow = connect(
  (state, ownProps) => ({
    minPurchaseLimit:
      actOnData(
        state.payApply.current.selectedPaymentMethod,
        ({ minPurchaseLimit }) => minPurchaseLimit,
      ) || 0,
    disabled: isSigned(ownProps.result),
    debitAmount: state.payApply.current.selectedAmount,
    debitReference: state.payApply.current.debitReference,
  }),
  dispatch => ({
    changeAmount: newDebitAmount => {
      dispatch(setDebitAmount(newDebitAmount))
    },
    changeReference: newReference => {
      dispatch(setDebitReference(newReference))
    },
  }),
)(DebitAmountRow)

const ApplicationAmountBox = ({
  result,
  onPageLoading,
  updateApplication,
  errorMessage,
  infoText,
}) => (
  <Frame>
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      <Column noPadding col6>
        <Row
          style={
            (result.application.approvedAmount <
              result.application.requestedAmount && {
              color: colors.orange,
            }) ||
            {}
          }
          label="payApply.sign.approvedAmount"
        >
          <div
            style={{
              display: 'inline-block',
            }}
          >
            <NumberFormat
              value={result.application.approvedAmount}
              displayType="text"
              thousandSeparator=" "
              renderText={val => getText('default.currency', val)}
            />
            <IconButton
              blue
              icon="midasSync2"
              spin={onPageLoading}
              style={{
                float: 'right',
                marginTop: '-0.6rem',
                position: 'absolute',
                right: '0',
              }}
              onClick={updateApplication}
              data-cy="refresh-application"
            />
          </div>
        </Row>
        <Row label="payApply.sign.requestedAmount">
          <NumberFormat
            value={result.application.requestedAmount}
            displayType="text"
            thousandSeparator=" "
            renderText={val => getText('default.currency', val)}
          />
        </Row>
        <ConnectedDebitAmountRow result={result} />
      </Column>
      <Column noPadding col1 />
      <Column noPadding col5>
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {getText(infoText || 'payApply.sign.infoText', {
            mupId: `${result.application.mupId || ''}`
              .match(/.{0,3}/g)
              .join(' '),
          })}
        </div>
      </Column>
      <Conditional when={!!errorMessage && !isSigned(result)}>
        <p style={{ color: '#cd202d', fontSize: '1.2rem', marginBottom: 0 }}>
          {errorMessage}
        </p>
      </Conditional>
    </div>
  </Frame>
)

ApplicationAmountBox.propTypes = {
  result: PropTypes.object.isRequired,
  onPageLoading: PropTypes.bool.isRequired,
  updateApplication: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  infoText: PropTypes.string,
}

ApplicationAmountBox.defaultProps = {
  errorMessage: '',
  infoText: '',
}

const PaymentAmountBox = ({ result, debitReference }) => (
  <Frame>
    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
      <Column noPadding col6>
        <Row label="payApply.sign.debitAmount">
          <NumberFormat
            value={result.payment.paymentStatus.totalOrderAmount}
            displayType="text"
            thousandSeparator=" "
            fixedDecimalScale
            decimalScale={2}
            renderText={val => getText('default.currency', val)}
          />
        </Row>
        <Conditional when={!!debitReference}>
          <Row label="payApply.sign.debitReference">{debitReference}</Row>
        </Conditional>
      </Column>
    </div>
  </Frame>
)

PaymentAmountBox.propTypes = {
  result: PropTypes.object.isRequired,
  debitReference: PropTypes.string,
}

PaymentAmountBox.defaultProps = {
  debitReference: '',
}

export const ApplicationSummaryBox = ({
  result,
  onPageLoading,
  updateApplication,
  debitReference,
  errorMessage,
  infoText,
}) => {
  if (result.paymentMethod.type === PaymentMethods.RESURS_INVOICE) {
    return <InvoiceAmountBox result={result} />
  }

  if (!result.application) {
    return <PaymentAmountBox result={result} debitReference={debitReference} />
  }
  return (
    <ApplicationAmountBox
      result={result}
      debitReference={debitReference}
      onPageLoading={onPageLoading}
      updateApplication={updateApplication}
      errorMessage={errorMessage}
      infoText={infoText}
    />
  )
}

ApplicationSummaryBox.propTypes = {
  result: PropTypes.object.isRequired,
  onPageLoading: PropTypes.bool.isRequired,
  updateApplication: PropTypes.func.isRequired,
  debitReference: PropTypes.string,
  errorMessage: PropTypes.string,
  infoText: PropTypes.string,
}

ApplicationSummaryBox.defaultProps = {
  debitReference: '',
  errorMessage: '',
  infoText: '',
}
